<script lang="ts" setup>import { computed as _computed, ref as _ref } from 'vue';

import { watchDebounced } from '@vueuse/core'
import GiftRecommendationCard from '@consumer/modules/myAccount/shared/recommendations/GiftRecommendationCard.vue'
import MultiplaceRecommendationsSection
  from '@consumer/modules/myAccount/shared/recommendations/MultiplaceRecommendationsSection.vue'
import RecommendationsSection from '@consumer/modules/myAccount/shared/recommendations/RecommendationsSection.vue'
import RecommendationsSwiper from '@consumer/modules/myAccount/shared/recommendations/RecommendationsSwiper.vue'
import { giftRecommendations, locations as locationsApi } from '@consumer/api'
import { dedupeGifts, giftDedupeKey } from '@consumer/utils/giftDedupe'
import { useGiftBadging, type RecommendedGift } from '@consumer/composables/giftBadging'
import { useConciergeState } from '@consumer/modules/buyersConcierge/conciergeState'
import { recommendationReadiness } from '@consumer/modules/buyersConcierge/recommendationRequest'
import { trackConciergeEvent, conciergeQueryMetadata } from '@consumer/modules/buyersConcierge/conciergeEventTracking'
import type { ConciergeInputVariantName } from '@consumer/modules/buyersConcierge/ConciergeInputVariant.vue'

// Rich, event-page-parity results for the Buyer's Concierge, rendered directly by
// the concierge takeover. Driven by the concierge store's signals rather than a
// saved subject; the detected-signal chips above (in ConciergeInput) already
// summarize the basis.
type Gift = RecommendedGift

withDefaults(defineProps<{ variant?: ConciergeInputVariantName }>(), { variant: 'free_text', })

const state = useConciergeState()

const PAGE_SIZE = 18

const readiness = _computed(() => recommendationReadiness(state.signals))

// The engine's locality section keys off a canonical "City, ST" (its saved-subject
// callers get that from the location picker); the concierge parses free text to a
// bare city, so resolve it through the same featured-city autocomplete before it
// drives the query — otherwise the local section never matches. Already-canonical
// input ("Seattle, WA") passes through untouched.
let resolvedLocation = _ref<string | null>(null)

// Seq guard: the search is async, so a slower earlier city must not overwrite a
// newer one (or a since-cleared field). Bumped on every call so an in-flight
// search whose input is stale drops its result.
let resolveSeq = 0

async function resolveLocation (city: string | null) {
  const seq = ++resolveSeq
  if (!city) {
    resolvedLocation.value = null
    return
  }
  // Only skip resolution for the exact canonical "City, ST" the backend matches
  // on (comma-space + uppercase state); anything looser is normalized by the search.
  if (/, [A-Z]{2}$/.test(city)) {
    resolvedLocation.value = city
    return
  }
  try {
    const matches = await locationsApi.search<{ value: string }[]>({ data: { term: city } })
    if (seq !== resolveSeq) return
    resolvedLocation.value = matches?.[0]?.value ?? null
  }
  catch {
    if (seq === resolveSeq) resolvedLocation.value = null
  }
}

watch(() => state.signals.location.value, resolveLocation, { immediate: true })

// snake_case keys: sent straight through as query params to the concierge source.
const conciergeQuery = _computed(() => ({
  recommendation_source: 'concierge',
  relationship: state.signals.relationship.value ?? undefined,
  occasion: state.signals.occasion.value ?? undefined,
  interests: state.signals.interests.value,
  location: resolvedLocation.value ?? undefined,
}))

const interests = _computed(() => state.signals.interests.value)
const locations = _computed(() => (resolvedLocation.value ? [resolvedLocation.value] : []))

const { badgesFor, resolveLocationImages } = useGiftBadging({
  interests: (interests),
  locations: (locations),
})

let gifts = _ref<Gift[]>([])
let loading = _ref(false)
let loadingMore = _ref(false)
let hasMore = _ref(false)
let nextOffset = _ref(0)

// Bump to re-key (and thus refetch) the self-fetching local sections when the
// signals change under them.
let placesKey = _ref(0)

// Drops a stale/superseded response so a slower earlier fetch can't overwrite a
// newer one — and so an in-flight fetch can't land after the field is cleared.
// Mirrors ConciergeInput's parseSeq guard.
let fetchSeq = 0

async function fetchGifts ({ append = false } = {}) {
  if (append && (loadingMore.value || !hasMore.value)) return

  const seq = ++fetchSeq
  const requestedOffset = append ? nextOffset.value : 0
  if (append) loadingMore.value = true
  else loading.value = true

  try {
    const response = await giftRecommendations.index<{
      gifts: Gift[]
      hasMore?: boolean
      nextOffset?: number
    }>({
      query: {
        ...conciergeQuery.value,
        section: 'nationwide',
        limit: PAGE_SIZE,
        offset: requestedOffset,
      },
    })
    if (seq !== fetchSeq) return

    const fetched = response.gifts ?? []
    gifts.value = append ? dedupeGifts([...gifts.value, ...fetched]) : fetched
    hasMore.value = response.hasMore ?? false
    // Advance by the server window (offset requested + items returned), not the
    // deduped client length — dedupe can shrink `gifts` below the server offset and
    // cause a page to be re-requested.
    nextOffset.value = response.nextOffset ?? (requestedOffset + fetched.length)
  }
  finally {
    // Always release `loadingMore` — only one append is ever in flight, so a
    // superseded one must still clear it or pagination stays blocked. `loading`
    // stays owned by the latest fetch, so only clear it when this call is latest.
    if (append) loadingMore.value = false
    else if (seq === fetchSeq) loading.value = false
  }
}

// The query this surface last fetched with, serialized. Signal writes that don't
// change what's sent (a display-only rewording, an unresolvable value committing
// null) still replace signal objects, and a deep watcher sees every new object —
// without this, identical params would refetch and flash skeletons over
// identical cards.
let fetchedQueryKey = ''

// Re-personalize as the buyer's signals settle. Only fetch once there's enough to
// search — a transient not-ready state mid-parse must NOT wipe the last results,
// so we simply skip fetching then. Clearing the field (below) is what clears them.
// No `immediate`: ConciergeInput re-parses the (persisted) text on mount, which
// moves the signals and triggers this watcher once — an immediate fire here would
// just duplicate that first fetch.
watchDebounced(
  () => conciergeQuery.value,
  () => {
    // Skip a transient not-ready state (mid-parse) and the beat after a clear where
    // the field is empty but the signals haven't caught up yet — either would fetch
    // stale/unwanted results.
    if (!readiness.value.ready || !state.raw.trim()) return
    const queryKey = JSON.stringify(conciergeQuery.value)
    if (queryKey === fetchedQueryKey) return
    fetchedQueryKey = queryKey
    placesKey.value++
    resolveLocationImages()
    // Clear the key on failure so a transient error doesn't permanently suppress
    // a retry for identical params — and so the unawaited fetch can't reject unhandled.
    fetchGifts().catch(() => { fetchedQueryKey = '' })
  },
  { debounce: 300, deep: true },
)

// Emptying the field resets everything; short of that we keep the last results on
// screen while a new fetch runs, so cards never blink out mid-typing. Bump both seq
// guards so an in-flight fetch or city resolution can't land after the clear, and
// reset the paging flags so pagination isn't left stuck.
watch(() => state.raw, (raw) => {
  if (!raw.trim()) {
    fetchSeq++
    resolveSeq++
    fetchedQueryKey = ''
    gifts.value = []
    loading.value = false
    loadingMore.value = false
    hasMore.value = false
    nextOffset.value = 0
    resolvedLocation.value = null
  }
})

// Per-card viewport impressions are deliberately not tracked.
watch(() => loading.value, (isLoading, wasLoading) => {
  if (!wasLoading || isLoading) return
  // Clearing/cancelling the field forces loading→false (and blanks fetchedQueryKey);
  // only emit results events for a real fetch completion, not a reset.
  if (!fetchedQueryKey) return
  const query = conciergeQueryMetadata(__props.variant, state.raw, state.signals)
  if (gifts.value.length > 0 || locations.value.length > 0) {
    const readyMeta = { gifts: gifts.value.length, locations: locations.value.length, ...query }
    trackConciergeEvent('results_ready', { variant: __props.variant, metadata: readyMeta })
  }
  else {
    trackConciergeEvent('results_empty', { variant: __props.variant, metadata: query })
  }
})

// Delegated so it covers every card (swiper + local sections) without touching the shared
// GiftRecommendationCard.
function onResultClick (event: MouseEvent) {
  const target = event.target as HTMLElement | null
  const anchor = target?.closest('a[name="calendar-gift-recommendation"]') ?? null
  if (!anchor) return

  const section = anchor.closest('[data-concierge-section]')
  const cards = section ? Array.from(section.querySelectorAll('a[name="calendar-gift-recommendation"]')) : []
  trackConciergeEvent('result_click', {
    variant: __props.variant,
    metadata: {
      href: anchor.getAttribute('href'),
      section: section?.getAttribute('data-concierge-section') ?? 'unknown',
      position: cards.indexOf(anchor),
    },
  })
}
</script>

<template>
  <div v-if="readiness.ready || gifts.length > 0" class="concierge-recommendations" @click="onResultClick">
    <MultiplaceRecommendationsSection
      v-for="location in locations"
      :key="`${placesKey}-local-${location}`"
      data-concierge-section="local"
      :query="conciergeQuery"
      :location="location"
      :title="`Multi-Place Gifts in ${location}`"
      :badgesFor="badgesFor"
    />

    <RecommendationsSection
      v-if="loading || gifts.length > 0"
      data-concierge-section="nationwide"
      title="Popular Multi-Place Gifts"
    >
      <RecommendationsSwiper
        :items="gifts"
        :itemKey="giftDedupeKey"
        :loading="loading"
        :loadingMore="loadingMore"
        @nearEnd="fetchGifts({ append: true })"
      >
        <template #skeleton>
          <GiftRecommendationCard loading/>
        </template>

        <template #default="{ item }">
          <GiftRecommendationCard :idea="item" :badges="badgesFor(item)"/>
        </template>
      </RecommendationsSwiper>
    </RecommendationsSection>

    <p v-if="!loading && !gifts.length && !locations.length" class="concierge-recommendations__empty">
      No matches yet — try another detail.
    </p>
  </div>
</template>

<style lang="scss" scoped>
.concierge-recommendations {
  display: grid;
  gap: 8px;
  margin-top: 16px;
  width: 100%;
}

.concierge-recommendations__empty {
  color: var(--color-text-tertiary);
  font-size: 14px;
  margin: 8px 0 0;
}
</style>
